import React from "react";
import { OrchestrationStepState } from "../../../../types/orchestration";
import { formatDuration } from "../../../../utils/orchestrationLayout";
import { NODE_CLASS } from "./orchestrationStyles";
import StepStatusIcon from "./StepStatusIcon";

interface StepNodeProps {
  step: OrchestrationStepState;
  x: number;
  y: number;
  width: number;
  pinned: boolean;
  onEnter: (step: OrchestrationStepState, e: React.MouseEvent) => void;
  onMove: (e: React.MouseEvent) => void;
  onLeave: () => void;
  onSelect: (step: OrchestrationStepState, el: HTMLElement) => void;
}

/** A single DAG node, absolutely positioned within the canvas. */
const StepNode: React.FC<StepNodeProps> = ({
  step,
  x,
  y,
  width,
  pinned,
  onEnter,
  onMove,
  onLeave,
  onSelect,
}) => {
  const fan = step.fanOut;
  const pct =
    fan && fan.total > 0 ? Math.round((fan.succeeded / fan.total) * 100) : 0;

  let footLabel: React.ReactNode;
  if (step.status === "running") {
    footLabel = <span className="text-rp-amber">running…</span>;
  } else if (step.status === "pending") {
    footLabel = <span className="text-gray-400 dark:text-gray-500">pending</span>;
  } else if (step.status === "skipped") {
    footLabel = <span className="text-gray-400 dark:text-gray-500">skipped</span>;
  } else if (step.durationMs != null) {
    footLabel = (
      <span className="text-gray-700 dark:text-gray-300">
        {formatDuration(step.durationMs)}
      </span>
    );
  } else {
    footLabel = <span className="text-gray-400 dark:text-gray-500">—</span>;
  }

  return (
    <div
      data-testid={`step-node-${step.id}`}
      data-status={step.status}
      role="button"
      tabIndex={0}
      onMouseEnter={(e) => onEnter(step, e)}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      onClick={(e) => {
        e.stopPropagation();
        onSelect(step, e.currentTarget);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect(step, e.currentTarget);
        }
      }}
      style={{ left: x, top: y, width }}
      className={`absolute z-[2] cursor-pointer rounded-xl border-[1.5px] p-3 transition-all duration-150 hover:-translate-y-0.5 hover:shadow-lg ${
        NODE_CLASS[step.status]
      } ${pinned ? "outline outline-2 outline-rp-amber outline-offset-2" : ""}`}
    >
      <div className="flex items-center gap-2">
        <StepStatusIcon status={step.status} size={15} />
        <span
          className={`truncate text-[13px] font-bold leading-tight ${
            step.status === "pending"
              ? "text-gray-500 dark:text-gray-400"
              : "text-gray-900 dark:text-white"
          }`}
        >
          {step.name}
          {step.status === "skipped" && (
            <span className="font-normal italic text-gray-400 dark:text-gray-500">
              {" "}
              (skipped)
            </span>
          )}
        </span>
      </div>

      {fan && (
        <div className="mt-2">
          <div className="mb-1 flex justify-between font-mono text-[10px] text-gray-500 dark:text-gray-400">
            <span>
              fan-out {fan.succeeded}/{fan.total}
              {fan.failed ? ` · ${fan.failed} failed` : ""}
            </span>
            <span>{pct}%</span>
          </div>
          <div className="h-[5px] overflow-hidden rounded-full bg-gray-200 dark:bg-rp-surface-sunken">
            <div
              className={`h-full rounded-full ${
                step.status === "failed"
                  ? "bg-red-500"
                  : step.status === "succeeded"
                  ? "bg-green-500"
                  : "bg-rp-amber"
              }`}
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>
      )}

      <div className="mt-2 flex items-center justify-between gap-1.5 font-mono text-[11px]">
        {footLabel}
        <span className="text-[10px] text-gray-400 dark:text-gray-500">
          {step.estimatedDurationLabel ? `est ${step.estimatedDurationLabel}` : "—"}
        </span>
      </div>
    </div>
  );
};

export default StepNode;
